/**
 * Internal EventSource wrapper with state machine
 *
 * States: INIT → STABILIZING → CONNECTED → DOWN
 *
 * Manages SSE connection lifecycle with stabilization period to ensure
 * connection is truly established before reporting success.
 */

type SSEState = 'INIT' | 'STABILIZING' | 'CONNECTED' | 'DOWN';

export class InternalEventSource {
  private state: SSEState;
  private reportSuccess: () => void;
  private reportFailure: () => void;
  private messageHandler: (eventType: string, data: string) => void;
  private errorHandler: (error: Error) => void;
  private source: EventSource | null;
  private stabilizationTimerStart: number;
  private stabilizationTimer: NodeJS.Timeout | null;
  public debug: boolean = false; // Set to true to enable verbose logging

  constructor(
    url: string,
    reportSuccess: () => void,
    reportFailure: () => void,
    messageHandler: (eventType: string, data: string) => void,
    errorHandler: (error: Error) => void
  ) {
    this.state = 'INIT';
    this.reportSuccess = reportSuccess;
    this.reportFailure = reportFailure;
    this.messageHandler = messageHandler;
    this.errorHandler = errorHandler;
    this.stabilizationTimerStart = 0;
    this.stabilizationTimer = null;

    this.source = new EventSource(url);
    this.source.onopen = () => this.onOpenHandler();
    this.source.onerror = (event) => this.onErrorHandler(event);

    // Listen for all SSE event types
    // TODO(tadhunt): caller should provide these, this is fine for now, but will need to be changed later
    const eventTypes = ['notification', 'sync-started', 'sync-progress', 'sync-completed', 'sync-failed', 'marketing-email-send-started', 'marketing-email-send-progress', 'marketing-email-send-accepted', 'marketing-email-send-failed', 'auth-error', 'token-refresh', 'keep-alive'];
    eventTypes.forEach((eventType) => {
      this.source!.addEventListener(eventType, (event: MessageEvent) => {
        this.onMessageHandler(eventType, event.data);
      });
    });

    // Generic message handler
    this.source.onmessage = (event) => {
      this.onMessageHandler('message', event.data);
    };
  }

  private onOpenHandler(): void {
    if(this.debug) {
      console.log(`[InternalEventStream] onOpenHandler in ${this.state}`);
    }
    if (this.state !== 'INIT') {
      if(this.debug) {
        console.log(`[InternalEventStream] ignore unexpected open in state ${this.state}`);
      }
      return;
    }

    this.state = 'STABILIZING';
    this.stabilizationTimerStart = Date.now();
    this.stabilizationTimer = setTimeout(() => this.stabilizationTimerExpired(), 100000);
    // now we wait for the first message: the server will deliver one ASAP to confirm connection establishment
  }

  private onErrorHandler(event: Event): void {
    if(this.debug) {
      console.log(`[InternalEventStream] onErrorHandler: state ${this.state}`);
    }

    switch (this.state) {
      case 'INIT':
        // failed before onOpenHandler() was called, should never happen
        this.reportFailure();
        break;
      case 'STABILIZING':
        if (this.stabilizationTimer) {
          clearTimeout(this.stabilizationTimer);
          this.stabilizationTimer = null;
        }
        this.reportFailure();
        break;
      case 'CONNECTED':
        this.errorHandler(new Error('SSE connection failed'));
        break;
      case 'DOWN':
        if(this.debug) {
          console.log(`[InternalEventStream] ignore error while in state DOWN`);
        }
        break;
    }

    if (this.source) {
      this.source.close();
      this.source = null;
    }

    this.state = 'DOWN';
  }

  private onMessageHandler(eventType: string, data: string): void {
    if(this.debug) {
      console.log(`[InternalEventStream] onMessageHandler: eventType ${eventType} data ${data}`);
    }
    switch (this.state) {
      case 'INIT':
        if(this.debug) {
          console.log(`[InternalEventStream] drop message while in state INIT: ${eventType}`);
        }
        break;
      case 'STABILIZING':
        this.state = 'CONNECTED';
        if (this.stabilizationTimer) {
          clearTimeout(this.stabilizationTimer);
          this.stabilizationTimer = null;
        }
        this.reportSuccess();
	if (eventType !== "keep-alive") {
		this.messageHandler(eventType, data);
	}
	break;
      case 'CONNECTED':
	if (eventType !== "keep-alive") {
		this.messageHandler(eventType, data);
	}
        break;
      case 'DOWN':
        if(this.debug) {
          console.log(`[InternalEventStream] drop message while in state DOWN: ${eventType}`);
        }
        break;
    }
  }

  private stabilizationTimerExpired(): void {
    const elapsed = Date.now() - this.stabilizationTimerStart;

    if(this.debug) {
      console.log(`[InternalEventStream] stabilization timer fired after ${elapsed} ms`);
    }

    this.state = 'DOWN';
    if (this.source !== null) {
	    this.source.close();
	    this.source = null;
    }
    this.reportFailure();
  }
}
